import { AdhaUaePassWebView } from '@/modules/auth/components/AdhaUaePassWebView';
import { FullScreenLoader } from '@/components/molecules';
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from '@/core/constants/dynatraceCatalog';
import { useCatalogDynatraceView } from '@/core/hooks/useCatalogDynatraceView';
import { useTranslation, useUIColors } from '@/core/hooks';
import { logger } from '@/core/logger';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import { loadAdhaTranslationsOnly } from '@/core/services/translationService';
import type {
  AdhaUaePassPendingResult,
  AdhaUaePassRawCallback,
} from '@/modules/auth/constants/adhaUaePass';
import { resolveAdhaUaePassUserMessage } from '@/modules/auth/constants/adhaUaePassError.map';
import { useAdhaLogin } from '@/modules/auth/hooks/useAdhaLogin';
import {
  extractResumeUrlFromAppReturn,
  getAdhaAppReturnPrefix,
  getNativeAdhaRedirectUri,
  getNativeWebViewSessionId,
  getStoredAuthenticatorUrls,
  isAdhaAppReturnUrl,
  isAdhaFailureReturnUrl,
  isFreshNativeAdhaLogin,
  parseNativeCallbackFromUrl,
  rememberRuntimeLink,
  restoreAuthenticatorCallbackUrls,
  shouldHandleAuthenticatorAppReturn,
  startNativeAdhaUaePassAuthorization,
  clearStoredAuthenticatorUrls,
  endNativeAdhaLogin,
  getAdhaCodeLoginInFlight,
  getAnyAdhaCodeLoginInFlight,
  isAdhaCodeLoginInProgress,
  markAdhaCodeLoginInProgress,
  setAdhaCodeLoginInFlight,
} from '@/modules/auth/services/adhaUaePassNative';
import { adhaUaePassService } from '@/modules/auth/services/adhaUaePassService';
import { useLocalSearchParams, useRouter } from 'expo-router';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, BackHandler, Linking, Platform, StyleSheet, View } from 'react-native';

function appendEncodedResumeUrl(baseUrl: string, resumeUrl: string): string {
  if (!resumeUrl) {
    return baseUrl;
  }
  return `${baseUrl}&url=${encodeURIComponent(resumeUrl)}`;
}

function buildCancelledOnAppCallbackUrl(
  nativeRedirectUri: string,
  resumeUrl: string,
): string {
  return appendEncodedResumeUrl(
    `${nativeRedirectUri}?error=cancelledOnApp`,
    resumeUrl,
  );
}

function buildFailAppReturnUrl(resumeUrl: string): string {
  return appendEncodedResumeUrl(
    `${getAdhaAppReturnPrefix()}?adhaReturn=fail`,
    resumeUrl,
  );
}

/** `Platform.OS === 'android' && isAdhaCodeLoginInProgress()` collapsed into
 * one boolean check — this exact pair is tested in several places below, and
 * inlining the `&&` at each call site is what pushes those functions' own
 * cognitive-complexity score over the limit. */
function isAndroidCodeLoginInProgress(): boolean {
  return Platform.OS === 'android' && isAdhaCodeLoginInProgress();
}

/** Android waiting page has neither code nor error — not a finished callback. */
function isUnfinishedAndroidCallback(raw: AdhaUaePassRawCallback): boolean {
  return Platform.OS === 'android' && !raw.code && !raw.error;
}

/** Android error callback with no code ends the native login before the dialog. */
function isAndroidErrorWithoutCode(raw: AdhaUaePassRawCallback): boolean {
  return Platform.OS === 'android' && Boolean(raw.error) && !raw.code;
}

function hasPendingCallbackFailure(pending: AdhaUaePassPendingResult): boolean {
  return Boolean(pending.errorKey || pending.error);
}

function isUaePassCancelResult(
  raw: AdhaUaePassRawCallback,
  pending: AdhaUaePassPendingResult,
): boolean {
  return [raw.error, pending.errorKey, pending.error].some(
    (value) => typeof value === 'string' && value.toLowerCase().includes('cancel'),
  );
}

function shouldResumeInFlightAndroidLogin(code: string | undefined): boolean {
  return !code && isAndroidCodeLoginInProgress();
}

function trackPendingCallbackFailure(
  raw: AdhaUaePassRawCallback,
  pending: AdhaUaePassPendingResult,
): void {
  const isCancel = isUaePassCancelResult(raw, pending);
  trackCatalogEvent({
    screen: ALMETLAA_SCREEN.AUTH,
    actionName: isCancel
      ? ALMETLAA_ACTION.AUTH.UAE_PASS_CANCEL
      : ALMETLAA_ACTION.AUTH.UAE_PASS_ERROR,
    eventType: isCancel ? 'action' : 'error',
    ...(pending.error
      ? { properties: { p_failedMessage: pending.error } }
      : {}),
  });
}

type AdhaCallbackParams = {
  url?: string;
  adhaReturn?: string;
  code?: string;
  accessCode?: string;
  access_code?: string;
  error?: string;
  state?: string;
  error_description?: string;
};

type StoredAuthenticatorUrls = ReturnType<typeof getStoredAuthenticatorUrls>;

/** The `awaitingReturn`/`stored` app-return case is only "cancelled" once
 * Salesforce's own `adhaReturn=fail` flag confirms it — pulled out purely so
 * its `&&`/`||` don't count against the caller's own complexity score. */
function isCancelledAppReturn(
  adhaReturn: string,
  awaitingReturn: boolean,
  stored: StoredAuthenticatorUrls,
): boolean {
  return adhaReturn === 'fail' && (awaitingReturn || Boolean(stored));
}

/** Same reasoning as {@link isCancelledAppReturn} — whether a resume URL is
 * actually usable right now. */
function shouldResumeWithUrl(
  initialResume: string | undefined,
  awaitingReturn: boolean,
  stored: StoredAuthenticatorUrls,
  adhaReturn: string,
): boolean {
  return Boolean(initialResume) &&
    (awaitingReturn || Boolean(stored) || adhaReturn === 'success');
}

/** Awaits an Android code-login that was already in flight when this screen
 * mounted, so the caller doesn't start a second, duplicate authorization. */
async function waitForInFlightAndroidLogin(): Promise<void> {
  const inFlight = getAnyAdhaCodeLoginInFlight();
  if (inFlight) {
    await inFlight;
  }
}

/** Awaits a leftover login promise from a previous mount, surfacing its
 * failure the same way a fresh attempt would. */
async function waitForLeftoverLogin(
  leftoverLogin: Promise<void>,
  showContentfulDialog: (resolveMessage: () => string) => Promise<void>,
  t: ReturnType<typeof useTranslation>['t'],
): Promise<void> {
  try {
    await leftoverLogin;
  } catch (error) {
    await showContentfulDialog(() => resolveAdhaUaePassUserMessage(error, t));
  }
}

/**
 * The actual "figure out what state we're resuming into" work for the main
 * mount effect below — pulled out to its own top-level function so it isn't
 * nested inside that effect (nesting depth is most of what drives cognitive
 * complexity here); behavior is unchanged from when this lived inline.
 */
async function resolveAdhaAuthorizationFlow(options: {
  params: AdhaCallbackParams;
  resumeUrl: string;
  adhaReturn: string;
  hasOauthParams: boolean;
  handleCallbackUrl: (url: string) => Promise<void>;
  setAuthUrl: (url: string | null) => void;
  setRedirectUri: (uri: string) => void;
  showContentfulDialog: (resolveMessage: () => string) => Promise<void>;
  t: ReturnType<typeof useTranslation>['t'];
}): Promise<void> {
  const {
    params,
    resumeUrl,
    adhaReturn,
    hasOauthParams,
    handleCallbackUrl,
    setAuthUrl,
    setRedirectUri,
    showContentfulDialog,
    t,
  } = options;

  try {
    if (isAndroidCodeLoginInProgress()) {
      await waitForInFlightAndroidLogin();
      return;
    }

    const stored = await restoreAuthenticatorCallbackUrls();
    const nativeRedirectUri = getNativeAdhaRedirectUri();
    setRedirectUri(nativeRedirectUri);
    const freshStart = isFreshNativeAdhaLogin();
    const awaitingReturn = shouldHandleAuthenticatorAppReturn();

    if (hasOauthParams) {
      await handleCallbackUrl(
        `${nativeRedirectUri}?${buildOAuthQueryFromParams(params)}`,
      );
      return;
    }

    if (freshStart) {
      const started = await startNativeAdhaUaePassAuthorization();
      setAuthUrl(started.authUrl);
      setRedirectUri(started.redirectUri);
      return;
    }

    if (isCancelledAppReturn(adhaReturn, awaitingReturn, stored)) {
      await handleCallbackUrl(
        buildCancelledOnAppCallbackUrl(nativeRedirectUri, resumeUrl),
      );
      return;
    }

    const initialResume = resumeUrl || stored?.successUrl;
    if (shouldResumeWithUrl(initialResume, awaitingReturn, stored, adhaReturn)) {
      setAuthUrl(initialResume as string);
      return;
    }

    const leftoverLogin = getAnyAdhaCodeLoginInFlight();
    if (leftoverLogin) {
      await waitForLeftoverLogin(leftoverLogin, showContentfulDialog, t);
      return;
    }

    // Expo Android remounts after UAE Pass. Stay on this screen instead of
    // starting a second authorize (that jumps to another page mid-login).
    if (Platform.OS === 'android') {
      return;
    }

    const started = await startNativeAdhaUaePassAuthorization();
    setAuthUrl(started.authUrl);
    setRedirectUri(started.redirectUri);
  } catch (error) {
    if (isAndroidCodeLoginInProgress()) {
      return;
    }
    logger.error('AdhaUaePassCallbackScreen: native authorize failed', error);
    await showContentfulDialog(() => t('auth.adhaUaePass.errorGeneric'));
  }
}

function readOAuthCodeFromParams(params: {
  code?: string;
  accessCode?: string;
  access_code?: string;
}): string {
  if (typeof params.code === 'string' && params.code) {
    return params.code;
  }
  if (typeof params.accessCode === 'string' && params.accessCode) {
    return params.accessCode;
  }
  if (typeof params.access_code === 'string' && params.access_code) {
    return params.access_code;
  }
  return '';
}

function buildOAuthQueryFromParams(params: {
  code?: string;
  accessCode?: string;
  access_code?: string;
  error?: string;
  state?: string;
  error_description?: string;
}): string {
  const oauthCode = readOAuthCodeFromParams(params);
  const oauthError = typeof params.error === 'string' ? params.error : '';
  const query = new URLSearchParams();
  if (oauthCode) {
    query.set('code', oauthCode);
  }
  if (oauthError) {
    query.set('error', oauthError);
  }
  if (typeof params.state === 'string') {
    query.set('state', params.state);
  }
  if (typeof params.error_description === 'string') {
    query.set('error_description', params.error_description);
  }
  return query.toString();
}

/**
 * Native UAE Pass OAuth WebView.
 * Same resolve/mapping as web `/uae-pass` → welcome:
 * - redirect error → MessageOkDialog on login
 * - OAuth missing_code → login, no dialog
 * - code → login API; API `code` failure → MessageOkDialog (labels only)
 */
export function AdhaUaePassCallbackScreen() {
  useCatalogDynatraceView(
    ALMETLAA_SCREEN.AUTH,
    ALMETLAA_ACTION.AUTH.UAE_PASS_CALLBACK_VIEW,
    true,
  );
  const colors = useUIColors();
  const router = useRouter();
  const params = useLocalSearchParams<{
    url?: string;
    adhaReturn?: string;
    code?: string;
    accessCode?: string;
    access_code?: string;
    error?: string;
    state?: string;
    error_description?: string;
  }>();
  const { t, currentLanguage } = useTranslation(true);
  const { completeAdhaLoginWithCode, isLoading } = useAdhaLogin();
  const loginInProgress = isLoading || isAndroidCodeLoginInProgress();
  const [authUrl, setAuthUrl] = useState<string | null>(null);
  const [redirectUri, setRedirectUri] = useState('');
  const handledRef = useRef(false);
  const startedRef = useRef(false);
  const webViewSessionId = getNativeWebViewSessionId();

  const goToLogin = useCallback(() => {
    if (Platform.OS === 'android' && getAnyAdhaCodeLoginInFlight()) {
      return;
    }
    adhaUaePassService.clearOAuthState();
    endNativeAdhaLogin();
    clearStoredAuthenticatorUrls();
    router.replace('/login');
  }, [router]);

  const finishWithDialog = useCallback(
    (message: string) => {
      adhaUaePassService.setNativePendingUi({ kind: 'dialog', message });
      goToLogin();
    },
    [goToLogin],
  );

  // Same as web: guests do not have the `adha`-tagged Contentful tree yet.
  // Load it, then resolve `auth.adhaUaePass.*` before the login dialog opens.
  const showContentfulDialog = useCallback(
    async (resolveMessage: () => string) => {
      await loadAdhaTranslationsOnly(currentLanguage);
      finishWithDialog(resolveMessage());
    },
    [currentLanguage, finishWithDialog],
  );

  const completeAdhaLoginOrReuseInFlight = useCallback(
    async (oauthCode: string) => {
      const existing = getAdhaCodeLoginInFlight(oauthCode);
      if (existing) {
        try {
          await existing;
        } catch (error) {
          await showContentfulDialog(() => resolveAdhaUaePassUserMessage(error, t));
        }
        return;
      }

      if (Platform.OS === 'android') {
        markAdhaCodeLoginInProgress();
      }
      const loginPromise = completeAdhaLoginWithCode(oauthCode);
      setAdhaCodeLoginInFlight(oauthCode, loginPromise);
      try {
        await loginPromise;
        trackCatalogEvent({
          screen: ALMETLAA_SCREEN.AUTH,
          actionName: ALMETLAA_ACTION.AUTH.UAE_PASS_SUCCESS,
          eventType: 'action',
        });
        clearStoredAuthenticatorUrls();
      } catch (error) {
        trackCatalogEvent({
          screen: ALMETLAA_SCREEN.AUTH,
          actionName: ALMETLAA_ACTION.AUTH.UAE_PASS_ERROR,
          eventType: 'error',
          properties: {
            p_failedMessage: error instanceof Error ? error.message : String(error),
          },
        });
        await showContentfulDialog(() => resolveAdhaUaePassUserMessage(error, t));
      }
    },
    [completeAdhaLoginWithCode, showContentfulDialog, t],
  );

  const handleCallbackUrl = useCallback(
    async (url: string) => {
      if (handledRef.current) {
        return;
      }

      const raw = parseNativeCallbackFromUrl(url);

      // Android: waiting page / state-only URL is not a finished callback.
      // Going to /login here is the video bug — login API has not started yet.
      if (isUnfinishedAndroidCallback(raw)) {
        return;
      }

      if (isAndroidErrorWithoutCode(raw)) {
        endNativeAdhaLogin();
      }

      handledRef.current = true;
      setAuthUrl(null);

      if (raw.code) {
        await completeAdhaLoginOrReuseInFlight(raw.code);
        return;
      }

      const pending = adhaUaePassService.resolvePendingResultFromCallback(raw);

      if (hasPendingCallbackFailure(pending)) {
        // Same cancel-vs-error split as web's useAdhaWelcomeOAuthCallback.
        trackPendingCallbackFailure(raw, pending);
        await showContentfulDialog(() =>
          pending.errorKey ? t(pending.errorKey) : (pending.error ?? ''),
        );
        return;
      }

      if (shouldResumeInFlightAndroidLogin(pending.code)) {
        handledRef.current = false;
        return;
      }

      if (!pending.code) {
        goToLogin();
        return;
      }

      await completeAdhaLoginOrReuseInFlight(pending.code);
    },
    [completeAdhaLoginOrReuseInFlight, showContentfulDialog, goToLogin, t],
  );

  const resumeAfterAuthenticator = useCallback(
    (incomingUrl: string) => {
      if (handledRef.current) {
        return;
      }
      if (isAndroidCodeLoginInProgress()) {
        return;
      }
      if (isFreshNativeAdhaLogin()) {
        return;
      }
      if (!shouldHandleAuthenticatorAppReturn() && !getStoredAuthenticatorUrls()) {
        return;
      }
      if (!isAdhaAppReturnUrl(incomingUrl)) {
        return;
      }


      const raw = parseNativeCallbackFromUrl(incomingUrl);
      if (raw.code || raw.error || isAdhaFailureReturnUrl(incomingUrl)) {
        void handleCallbackUrl(incomingUrl);
        return;
      }

      const resumeUrl = extractResumeUrlFromAppReturn(incomingUrl);
      if (!resumeUrl) {
        return;
      }

      setAuthUrl(resumeUrl);
    },
    [handleCallbackUrl],
  );

  useEffect(() => {
    const subscription = Linking.addEventListener('url', ({ url }) => {
      rememberRuntimeLink(url);
      resumeAfterAuthenticator(url);
    });

    Linking.getInitialURL()
      .then((url) => {
        if (!url) {
          return;
        }
        rememberRuntimeLink(url);
        if (Platform.OS === 'android' && isAdhaFailureReturnUrl(url)) {
          return;
        }
        resumeAfterAuthenticator(url);
      })
      .catch(() => undefined);

    return () => {
      subscription.remove();
    };
  }, [resumeAfterAuthenticator]);

  useEffect(() => {
    startedRef.current = true;

    if (Platform.OS === 'android') {
      const leftoverLogin = getAnyAdhaCodeLoginInFlight();
      if (leftoverLogin || isAdhaCodeLoginInProgress()) {
        handledRef.current = true;
        setAuthUrl(null);
        if (leftoverLogin) {
          void leftoverLogin.catch((error) => {
            void showContentfulDialog(() => resolveAdhaUaePassUserMessage(error, t));
          });
        }
        return;
      }
    }

    handledRef.current = false;
    setAuthUrl(null);

    const resumeUrl = typeof params.url === 'string' ? params.url : '';
    const adhaReturn = typeof params.adhaReturn === 'string' ? params.adhaReturn : '';
    const oauthCode = readOAuthCodeFromParams(params);
    const oauthError = typeof params.error === 'string' ? params.error : '';
    const hasOauthParams = Boolean(oauthCode || oauthError);

    void resolveAdhaAuthorizationFlow({
      params,
      resumeUrl,
      adhaReturn,
      hasOauthParams,
      handleCallbackUrl,
      setAuthUrl,
      setRedirectUri,
      showContentfulDialog,
      t,
    });
    // Restart when the user taps Continue with UAE Pass again (new session id).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webViewSessionId]);

  useEffect(() => {
    const resumeUrl = typeof params.url === 'string' ? params.url : '';
    const adhaReturn = typeof params.adhaReturn === 'string' ? params.adhaReturn : '';
    const oauthCode = readOAuthCodeFromParams(params);
    const oauthError = typeof params.error === 'string' ? params.error : '';
    const hasOauthParams = Boolean(oauthCode || oauthError);

    if (
      !startedRef.current ||
      handledRef.current ||
      isAndroidCodeLoginInProgress()
    ) {
      return;
    }

    if (hasOauthParams) {
      const base = redirectUri || getNativeAdhaRedirectUri();
      void handleCallbackUrl(`${base}?${buildOAuthQueryFromParams(params)}`);
      return;
    }

    if (isFreshNativeAdhaLogin()) {
      return;
    }

    if (!shouldHandleAuthenticatorAppReturn() && !getStoredAuthenticatorUrls()) {
      return;
    }

    if (adhaReturn === 'fail') {
      resumeAfterAuthenticator(buildFailAppReturnUrl(resumeUrl));
      return;
    }

    if (resumeUrl || adhaReturn === 'success') {
      const stored = getStoredAuthenticatorUrls();
      const nextResume = resumeUrl || stored?.successUrl;
      if (nextResume) {
        setAuthUrl(nextResume);
      }
    }
  }, [
    handleCallbackUrl,
    params.accessCode,
    params.access_code,
    params.adhaReturn,
    params.code,
    params.error,
    params.error_description,
    params.state,
    params.url,
    redirectUri,
    resumeAfterAuthenticator,
  ]);

  useEffect(() => {
    const subscription = BackHandler.addEventListener('hardwareBackPress', () => {
      // Once login has succeeded this screen is on its way out (replaced by
      // Home) — if this listener is still attached when back is pressed
      // afterwards (e.g. mid-transition), it must not send the user back to
      // login. Let the event fall through instead.
      if (handledRef.current) {
        return false;
      }
      goToLogin();
      return true;
    });
    return () => subscription.remove();
  }, [goToLogin]);

  const handleWebViewLoadError = useCallback(() => {
    if (handledRef.current) {
      return;
    }
    if (isAndroidCodeLoginInProgress()) {
      return;
    }
    // Custom-scheme hops to the UAE Pass app look like WebView failures.
    if (shouldHandleAuthenticatorAppReturn()) {
      return;
    }
    handledRef.current = true;
    setAuthUrl(null);
    void showContentfulDialog(() => t('auth.adhaUaePass.somethingWentWrong'));
  }, [showContentfulDialog, t]);

  let authContent: React.ReactNode = null;
  if (authUrl && !loginInProgress) {
    authContent = (
      <AdhaUaePassWebView
        key={getNativeWebViewSessionId() || authUrl}
        authUrl={authUrl}
        redirectUri={redirectUri}
        onCallbackUrl={(url) => {
          void handleCallbackUrl(url);
        }}
        onLoadError={handleWebViewLoadError}
      />
    );
  } else if (Platform.OS === 'android' && !loginInProgress) {
    authContent = (
      <View style={styles.androidLoading}>
        <ActivityIndicator size="large" color={colors.bloomRed} />
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]}>
      {authContent}
      <FullScreenLoader visible={loginInProgress} />
    </View>
  );
}

export default AdhaUaePassCallbackScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  androidLoading: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
